import { apiClient } from './api';
import type { AxiosError } from 'axios';

export type SettlementStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PROCESSED';

export interface SettlementQuery {
  status?: SettlementStatus;
  restaurantId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface Settlement {
  id: string;
  restaurantId: string;
  restaurantName: string;
  amount: number;
  status: SettlementStatus;
  periodStart: string;
  periodEnd: string;
  orderCount: number;
  cashOrderCount?: number;
  creditCardOrderCount?: number;
  createdAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  notes?: string;
  rejectionReason?: string;
}

export interface SettlementListResponse {
  settlements: Settlement[];
  total: number;
  page: number;
  limit: number;
}

export interface FinancialDashboard {
  totalRevenue: number;
  commissionRevenue: number;
  deliveryFees: number;
  netProfit: number;
  totalPlatformRevenue: number;
  totalCashInMarket: number;
  trends: Array<{ date: string; revenue: number; commission: number }>;
}

export interface Commission {
  id: string;
  amount: number;
  restaurantId: string | null;
  restaurantName: string | null;
  orderId: string | null;
  createdAt: string;
  description: string;
}

export type PayoutStatusType = 'PENDING' | 'PROCESSED' | 'FAILED';
export type PayoutRecipientType = 'RESTAURANT' | 'COURIER';

export interface Payout {
  id: string;
  recipientType: PayoutRecipientType;
  recipientId: string;
  amount: number;
  status: PayoutStatusType;
  ibanSnapshot: string;
  failureReason: string | null;
  externalReference: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayoutQuery {
  recipientType?: PayoutRecipientType;
  recipientId?: string;
  status?: PayoutStatusType;
  page?: number;
  limit?: number;
}

export interface PayoutListResponse {
  payouts: Payout[];
  total: number;
}

export interface WalletMismatch {
  walletId: string;
  ownerId: string;
  ownerType: string;
  recordedBalance: string;
  computedBalance: string;
  difference: string;
}

export interface ReconciliationResult {
  totalWallets: number;
  checkedWallets: number;
  mismatches: WalletMismatch[];
  orphanedLedgerEntries: number;
  runAt: string;
}

export const adminFinanceService = {
  async getFinancialDashboard(period: 'today' | 'week' | 'month' = 'month'): Promise<FinancialDashboard> {
    const response = await apiClient.get<FinancialDashboard>('/admin/finance/dashboard', {
      params: { period },
    });
    return response.data.data;
  },

  async listSettlements(query: SettlementQuery): Promise<SettlementListResponse> {
    const response = await apiClient.get<SettlementListResponse>('/admin/finance/settlements', {
      params: query,
    });
    return response.data.data;
  },

  async getSettlementDetails(settlementId: string): Promise<Settlement> {
    const response = await apiClient.get<Settlement>(`/admin/finance/settlements/${settlementId}`);
    return response.data.data;
  },

  async approveSettlement(settlementId: string, notes?: string) {
    const response = await apiClient.patch(`/admin/finance/settlements/${settlementId}/approve`, {
      notes,
    });
    return response.data.data;
  },

  async rejectSettlement(settlementId: string, reason: string) {
    const response = await apiClient.patch(`/admin/finance/settlements/${settlementId}/reject`, {
      reason,
    });
    return response.data.data;
  },

  async listCommissions(restaurantId?: string, startDate?: string, endDate?: string): Promise<Commission[]> {
    const response = await apiClient.get<Commission[]>('/admin/finance/commissions', {
      params: {
        ...(restaurantId && { restaurantId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data.data;
  },

  async generateReport(
    type: 'financial' | 'operational' | 'customer' | 'restaurant' | 'courier',
    startDate: string,
    endDate: string,
    format: 'csv' | 'json' | 'pdf' = 'json',
  ) {
    const response = await apiClient.get('/admin/finance/reports', {
      params: { type, startDate, endDate, format },
    });
    return response.data.data;
  },

  async getPendingCashPayments(courierId?: string) {
    const response = await apiClient.get('/admin/finance/pending-cash-payments', {
      params: courierId ? { courierId } : {},
    });
    return response.data.data;
  },

  async listPayouts(query: PayoutQuery): Promise<PayoutListResponse> {
    const response = await apiClient.get<PayoutListResponse>('/finance/admin/payouts', {
      params: query,
    });
    return response.data.data;
  },

  async generateRestaurantPayouts() {
    const response = await apiClient.post('/finance/admin/payouts/generate-restaurant');
    return response.data.data;
  },

  async generateCourierPayouts() {
    const response = await apiClient.post('/finance/admin/payouts/generate-courier');
    return response.data.data;
  },

  async processPayout(payoutId: string) {
    const response = await apiClient.post(`/finance/admin/payouts/${payoutId}/process`);
    return response.data.data;
  },

  async failPayout(payoutId: string, reason: string) {
    const response = await apiClient.post(`/finance/admin/payouts/${payoutId}/fail`, { reason });
    return response.data.data;
  },

  async exportPayoutsCsv(status?: PayoutStatusType) {
    const response = await apiClient.get('/finance/admin/payouts/export-csv', {
      params: status ? { status } : {},
      responseType: 'blob',
    });
    return response.data;
  },

  async runReconciliation(): Promise<ReconciliationResult> {
    try {
      const response = await apiClient.post<ReconciliationResult>('/admin/finance/reconciliation/run');
      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status !== 404) {
        throw error;
      }

      const legacyResponse = await apiClient.post<ReconciliationResult>(
        '/finance/admin/reconciliation/run',
      );
      return legacyResponse.data.data;
    }
  },
};
